import { useState, useEffect, useCallback } from "react";
import { api } from "../services/api";
import { ApiError } from "../services/api/BaseApi";
import type { FeedItem } from "../types/Post/post";

type PostDetailState = "loading" | "success" | "not-found" | "error";

interface UsePostDetailResult {
  feedItem: FeedItem | null;
  state: PostDetailState;
  error: string | null;
  refetch: () => void;
  updateItem: (updater: (item: FeedItem) => FeedItem) => void;
}

export function usePostDetail(postId: string | undefined): UsePostDetailResult {
  const [feedItem, setFeedItem] = useState<FeedItem | null>(null);
  const [state, setState] = useState<PostDetailState>("loading");
  const [error, setError] = useState<string | null>(null);

  const fetchPost = useCallback(async (id: string) => {
    setState("loading");
    setFeedItem(null);
    setError(null);

    try {
      const result = await api.social.getPost(id);
      setFeedItem(result);
      setState("success");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setState("not-found");
      } else {
        setState("error");
        setError(
          err instanceof Error ? err.message : "An unexpected error occurred"
        );
      }
    }
  }, []);

  useEffect(() => {
    if (!postId) {
      setState("error");
      setError("Invalid post ID");
      return;
    }

    fetchPost(postId);
  }, [postId, fetchPost]);

  const refetch = useCallback(() => {
    if (postId) {
      fetchPost(postId);
    }
  }, [postId, fetchPost]);

  const updateItem = useCallback((updater: (item: FeedItem) => FeedItem) => {
    setFeedItem((prev) => (prev ? updater(prev) : prev));
  }, []);

  return { feedItem, state, error, refetch, updateItem };
}
